import { useState } from 'react';
import { Copy, Unlink, CalendarX, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';

import { useDataQuality } from '@/hooks/useLocalStorage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { dataSource } from '@/lib/dataSource';
import { useToast } from '@/hooks/use-toast';

type IssueTab = 'DUPLICATE' | 'ORPHAN' | 'OVERLAP';

export function DataQuality() {
  const { data: issues, isLoading, isFetching, refetch } = useDataQuality();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<IssueTab>('DUPLICATE');
  const [fixingId, setFixingId] = useState<number | null>(null);

  /* -------------------- Counts from API -------------------- */
  const stats = {
    duplicates: issues?.summary?.duplicates ?? 0,
    orphans: issues?.summary?.orphans ?? 0,
    overlaps: issues?.summary?.overlaps ?? 0,
  };
  console.log("issues", issues)
  /* -------------------- Table Data Switch -------------------- */
  const tableData = (() => {
    if (!issues) return [];
    switch (activeTab) {
      case 'DUPLICATE':
        return issues.duplicates || [];
      case 'ORPHAN':
        return issues.orphans || [];
      case 'OVERLAP':
        return issues.overlaps || [];
      default:
        return [];
    }
  })();

  /* -------------------- Fix Logic -------------------- */
  // const handleFix = async (item: any) => {
  //   setFixingId(item.ID);
  //   try {
  //     await dataSource.deleteCode(item.Type, item.Code);
  //     toast({
  //       title: 'Resolved',
  //       description: 'Item successfully removed',
  //     });
  //     refetch();
  //   } catch (err: any) {
  //     toast({
  //       title: 'Error',
  //       description: err?.message || 'Failed to fix issue',
  //       variant: 'destructive',
  //     });
  //   } finally {
  //     setFixingId(null);
  //   }
  // };

  console.log("issues", issues)
  return (
    <div className="bg-gray-50 min-h-full p-4">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold">Data Quality Dashboard</h2>
        <p className="text-gray-600">Identify and resolve integrity issues</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card
          onClick={() => setActiveTab('DUPLICATE')}
          className={`cursor-pointer ${activeTab === 'DUPLICATE' ? 'ring-2 ring-red-500' : ''
            }`}
        >
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">Duplicates</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.duplicates}
              </p>
            </div>
            <Copy className="h-8 w-8 text-red-600" />
          </CardContent>
        </Card>

        <Card
          onClick={() => setActiveTab('ORPHAN')}
          className={`cursor-pointer ${activeTab === 'ORPHAN' ? 'ring-2 ring-orange-500' : ''
            }`}
        >
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">Orphans</p>
              <p className="text-2xl font-bold text-orange-600">
                {stats.orphans}
              </p>
            </div>
            <Unlink className="h-8 w-8 text-orange-600" />
          </CardContent>
        </Card>

        <Card
          onClick={() => setActiveTab('OVERLAP')}
          className={`cursor-pointer ${activeTab === 'OVERLAP' ? 'ring-2 ring-yellow-500' : ''
            }`}
        >
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">Overlaps</p>
              <p className="text-2xl font-bold text-yellow-600">
                {stats.overlaps}
              </p>
            </div>
            <CalendarX className="h-8 w-8 text-yellow-600" />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle>
            {activeTab === 'DUPLICATE' && 'Duplicate DataTypes'}
            {activeTab === 'ORPHAN' && 'Orphan Records'}
            {activeTab === 'OVERLAP' && 'Overlapping Attributes'}
          </CardTitle>

          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
            />
            Scan Again
          </Button>
        </CardHeader>

        <CardContent>
          {tableData.length !== 0 ?
            <Table>
              <TableHeader>
                <TableRow>
                  {
                    activeTab === 'DUPLICATE' &&
                    <TableHead>ID</TableHead>
                  }
                  <TableHead>Name</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Created At</TableHead>
                  {activeTab === 'DUPLICATE' ?
                    <TableHead>Application ID</TableHead>
                    :
                    <>
                      <TableHead>DataType</TableHead>
                      <TableHead>DataType Id</TableHead>
                    </>
                  }
                </TableRow>
              </TableHeader>

              <TableBody>
                {tableData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500">
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  tableData.map((item: any) => (
                    <TableRow key={`${item.id}`}>
                      {activeTab === 'DUPLICATE' &&
                        <TableCell>{item.id}</TableCell>
                      }
                      <TableCell>{item.name || item.code}</TableCell>
                      <TableCell>{item.dataLabel || item.label}</TableCell>
                      <TableCell>
                        {activeTab === 'DUPLICATE'
                          ? new Date(item.createdDateTime).toLocaleString()
                          : new Date(item.createdAt).toLocaleString()}
                      </TableCell>
                      {activeTab === 'DUPLICATE' &&
                        <TableCell>{item.applicationID}</TableCell>
                      }
                      {activeTab !== 'DUPLICATE' &&
                        <>
                          <TableCell>{item.dataTypeName}</TableCell>
                          <TableCell>{item.dataTypeId}</TableCell>
                        </>
                      }
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table> :
            (
              <div className="text-center py-8 text-gray-500">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <p>No data quality issues found. Your data looks good!</p>
              </div>
            )
          }
        </CardContent>
      </Card>
    </div>
  );
}
